import axios from "axios";
import { getDeviceId } from "./deviceId";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Device-Id"] = getDeviceId();
  return config;
});

export const api = {
  // Raw axios client (used by features that need full Response/headers — e.g. push.js)
  client,

  // Auth
  register: (data) => client.post("/auth/register", data).then((r) => r.data),
  login: (data) => client.post("/auth/login", data).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),

  // Rooms
  createRoom: (data) => client.post("/rooms/create", data).then((r) => r.data),
  joinRoom: (data) => client.post("/rooms/join", data).then((r) => r.data),
  getRoom: (code) => client.get(`/rooms/${code}`).then((r) => r.data),
  setMode: (code, data) => client.post(`/rooms/${code}/set-mode`, data).then((r) => r.data),
  nextRound: (code) => client.post(`/rooms/${code}/next`).then((r) => r.data),
  action: (code, data) => client.post(`/rooms/${code}/action`, data).then((r) => r.data),
  quizAnswer: (code, data) => client.post(`/rooms/${code}/quiz-answer`, data).then((r) => r.data),
  submitAnswer: (code, data) => client.post(`/rooms/${code}/submit-answer`, data).then((r) => r.data),
  reactToAnswer: (code, data) => client.post(`/rooms/${code}/react`, data).then((r) => r.data),

  // Group rooms (3–20 player party mode)
  createGroupRoom: (data) => client.post("/group-rooms/create", data).then((r) => r.data),
  joinGroupRoom: (code, data) => client.post(`/group-rooms/${code}/join`, data).then((r) => r.data),
  getGroupRoom: (code) => client.get(`/group-rooms/${code}`).then((r) => r.data),
  startGroupRoom: (code, data) => client.post(`/group-rooms/${code}/start`, data).then((r) => r.data),
  kickGroupPlayer: (code, playerId) => client.delete(`/group-rooms/${code}/players/${playerId}`).then((r) => r.data),
  voteGroup: (code, data) => client.post(`/group-rooms/${code}/vote`, data).then((r) => r.data),
  resultsGroup: (code) => client.get(`/group-rooms/${code}/results`).then((r) => r.data),
  nextGroupRound: (code) => client.post(`/group-rooms/${code}/next`).then((r) => r.data),
  switchGroupPack: (code, data) => client.post(`/group-rooms/${code}/switch-pack`, data).then((r) => r.data),

  // Confessions (premium group pack) — typed answers + reactions + votes + leaderboard
  confessionSubmit: (code, data) => client.post(`/group-rooms/${code}/confession/submit`, data).then((r) => r.data),
  confessionReact: (code, data) => client.post(`/group-rooms/${code}/confession/react`, data).then((r) => r.data),
  confessionVote: (code, data) => client.post(`/group-rooms/${code}/confession/vote`, data).then((r) => r.data),
  confessionReveal: (code) => client.post(`/group-rooms/${code}/confession/reveal`).then((r) => r.data),
  confessionNext: (code) => client.post(`/group-rooms/${code}/confession/next`).then((r) => r.data),

  // Streak persistence (server-backed, replaces localStorage)
  streakPing: () => client.post(`/streak/ping`).then((r) => r.data),
  streakGet: () => client.get(`/streak`).then((r) => r.data),
  reactGroup: (code, data) => client.post(`/group-rooms/${code}/react`, data).then((r) => r.data),

  // Whispers (legacy — kept for back-compat with users that already had whispers)
  whisperPartner: () => client.get("/whispers/partner").then((r) => r.data),
  whisperSend: (data) => client.post("/whispers/send", data).then((r) => r.data),
  whisperInbox: () => client.get("/whispers/inbox").then((r) => r.data),
  whisperAnswer: (id, data) => client.post(`/whispers/${id}/answer`, data).then((r) => r.data),
  whisperSeen: (id) => client.post(`/whispers/${id}/seen`).then((r) => r.data),

  // Chat — paired chat thread between connected partners (replaces Whispers UI)
  chatThread: () => client.get("/chat/thread").then((r) => r.data),
  chatSend: (data) => client.post("/chat/send", data).then((r) => r.data),
  chatReact: (mid, emoji) => client.post(`/chat/${mid}/react`, { emoji }).then((r) => r.data),
  chatSeen: () => client.post(`/chat/seen`).then((r) => r.data),
  getAudio: (audioId) => client.get(`/audio/${audioId}`).then((r) => r.data),
  getVideo: (videoId) => client.get(`/video/${videoId}`).then((r) => r.data),
  consumeVideo: (videoId) => client.post(`/video/${videoId}/consume`).then((r) => r.data),
  usageVideoToday: () => client.get("/usage/video-today").then((r) => r.data),
  quizContinue: (code, matched) =>
    client.post(`/rooms/${code}/quiz-continue?matched=${matched}`).then((r) => r.data),
  backToLobby: (code) => client.post(`/rooms/${code}/back-to-lobby`).then((r) => r.data),

  // Packs
  listPacks: () => client.get("/packs").then((r) => r.data),
  getPack: (id) => client.get(`/packs/${id}`).then((r) => r.data),

  // Custom questions
  listCustomQuestions: () => client.get("/custom-questions").then((r) => r.data),
  createCustomQuestion: (data) => client.post("/custom-questions", data).then((r) => r.data),
  deleteCustomQuestion: (id) => client.delete(`/custom-questions/${id}`).then((r) => r.data),
  generateRelationshipPack: (data) => client.post("/ai-packs/relationship", data).then((r) => r.data),

  // History
  getHistory: () => client.get("/history").then((r) => r.data),

  // Billing
  billingCatalog: () => client.get("/billing/catalog").then((r) => r.data),
  billingEntitlements: () => client.get("/billing/entitlements").then((r) => r.data),
  billingIntroStatus: (start = true) =>
    client.get(`/billing/intro-status?start=${start ? "true" : "false"}`).then((r) => r.data),
  usageToday: () => client.get("/usage/today").then((r) => r.data),
  billingCheckout: (sku) =>
    client
      .post("/billing/checkout", { sku, origin_url: window.location.origin })
      .then((r) => r.data),
  billingStatus: (sessionId) =>
    client.get(`/billing/status-check/${sessionId}`).then((r) => r.data),
};

export default api;
